/**
 * Shared Bugsnag noise-filter predicates.
 *
 * Imported by both the server reporter (instrumentation.ts onRequestError) and
 * the shared config (config.ts onError, which the client bundle also loads) —
 * keep this module dependency-free and side-effect-free so it stays inert in
 * the browser.
 */

/**
 * Matches the Node webstreams post-abort teardown race — and nothing else.
 *
 * When a stream is torn down mid-flight (client disconnect during streaming
 * SSR, an aborted fetch body), Node clears the TransformStream algorithms
 * (transformStreamDefaultControllerClearAlgorithms) while a pending
 * transformStreamDefaultControllerPerformTransform microtask is still queued;
 * when that microtask runs it calls the now-undefined algorithm and throws
 * `TypeError: controller[kState].transformAlgorithm is not a function` from
 * node:internal/webstreams/transformstream. The stack is 100% Node-internal
 * (no project frames) and the request was already gone — harmless teardown
 * noise, yet it was the top BugSnag error (204 events over six weeks).
 *
 * Upstream: https://github.com/nodejs/node/issues/62036, fixed by
 * https://github.com/nodejs/node/pull/62040 (ships in Node >= 24.15.0; no
 * 22.x backport at the time of writing). Remove this filter once the
 * Dockerfile NODE_VERSION pin moves to a release containing that fix.
 *
 * Deliberately narrow: TypeError + exact message + at least one stack frame,
 * with EVERY frame inside node:internal/. Do NOT widen this to other abort,
 * stream, or unhandled-rejection errors — anything with a project frame must
 * keep reaching Bugsnag.
 */
export function isNodeWebstreamsTeardownError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (error.name !== "TypeError") return false
  if (!error.message.includes("transformAlgorithm is not a function")) return false

  const frames = (error.stack ?? "").split("\n").filter((line) => /^\s+at\s/.test(line))

  // No frames means we cannot prove the error is Node-internal — let it report.
  if (frames.length === 0) return false

  return frames.every((frame) => frame.includes("node:internal/"))
}

/**
 * True when a stack frame belongs to the framework or the Node runtime rather
 * than to code of ours. Shared by every "suppress only if NO project frame is
 * present" predicate below, so they can never drift apart.
 *
 * Why not simply "contains .next": in a production build EVERYTHING we ship
 * lives under /app/.next/server/…, so counting that whole tree as framework
 * code made those predicates' promise — a project frame keeps the error
 * reporting — vacuous in production. A route handler of ours calling
 * `await request.formData()` on a bad body from a real browser would have been
 * suppressed with a false "bot POST, never reached a page" verdict.
 *
 * OURS (returns false): `/.next/server/app/` — our compiled route handlers and
 * pages — plus `/src/` and turbopack's `[project]/src` dev frames.
 *
 * FRAMEWORK (returns true): `node:internal`, `node_modules`, `next/dist`, and
 * shared chunks under `.next/server/chunks/` whose file name still carries the
 * Next dist path, which is how the framework appears in a compiled stack
 * (observed: `.next/server/chunks/ssr/0d-y_next_dist_esm_build_templates_app-page_*.js`).
 *
 * HONEST LIMITATION: those chunk names are a turbopack naming heuristic, not a
 * contract — a Next upgrade can rename them. The failure mode is a framework
 * frame counted as ours, which only makes an error REPORT, never suppresses
 * one. Anything under `.next/` we cannot attribute is treated as ours for the
 * same reason.
 */
export function isFrameworkFrame(frame: string): boolean {
  // Ours, even though a production build puts it under .next/.
  if (frame.includes("/.next/server/app/")) return false
  if (frame.includes("/src/") || frame.includes("[project]/src")) return false

  if (frame.includes("node:internal")) return true
  if (frame.includes("node_modules")) return true
  if (frame.includes("next/dist")) return true
  return frame.includes(".next/server/chunks/") && frame.includes("next_dist")
}

/**
 * Matches Next.js "failed to pipe response" — and nothing else.
 *
 * Next throws this from its server render pipe when the client disconnects
 * mid-stream (navigates away / closes the tab before the SSR/RSC stream has
 * finished writing to the socket). The request is already gone and the message
 * is framework-exclusive — application code never produces it — so there is
 * nothing to fix and no user left to affect. It was pure dev/prod deploy churn
 * noise (51 events, all framework frames).
 *
 * Deliberately narrow: exact message + at least one stack frame, with EVERY
 * frame framework-owned per isFrameworkFrame (which counts our compiled app
 * routes under .next/server/app as OURS). If a project frame appears, it
 * reports — a real pipe failure originating in our code must keep reaching
 * Bugsnag.
 */
export function isClientDisconnectStreamError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (error.message !== "failed to pipe response") return false

  const frames = (error.stack ?? "").split("\n").filter((line) => /^\s+at\s/.test(line))

  // No frames means we cannot prove it is framework-internal — let it report.
  if (frames.length === 0) return false

  return frames.every(isFrameworkFrame)
}

/**
 * Matches undici's malformed-multipart parse failure — and nothing else.
 *
 * Next.js treats ANY POST whose content-type starts with multipart/form-data as
 * a Server Action invocation: isMultipartAction is set from the method plus the
 * content-type alone, with no Next-Action header required
 * (node_modules/next/dist/server/lib/server-action-request-meta.js). The action
 * handler then calls fakeRequest.formData() before any render
 * (node_modules/next/dist/server/app-render/action-handler.js), so a scanner
 * POSTing a garbage multipart body to an arbitrary path (observed in production:
 * /RSC/38pgyzyx6tzgxgn.txt) makes undici throw
 * `TypeError: Failed to parse body as FormData.` from a stack that never leaves
 * node:internal/deps/undici. No route matched, no page rendered, no user exists
 * — there is nothing in our code to fix.
 *
 * src/proxy.ts 400s the action-shaped POSTs its matcher can see BEFORE Next
 * parses a body (isRejectableActionPost); this predicate is the backstop for
 * the path shapes that matcher never covers.
 *
 * Deliberately narrow: TypeError + the exact message INCLUDING its trailing
 * period + at least one stack frame, with EVERY frame framework-owned per
 * isFrameworkFrame. A real Server Action or route handler of ours failing to
 * parse its own body carries a project frame — including a compiled one under
 * .next/server/app — and must keep reaching Bugsnag.
 */
export function isMalformedActionBodyError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (error.name !== "TypeError") return false
  if (error.message !== "Failed to parse body as FormData.") return false

  const frames = (error.stack ?? "").split("\n").filter((line) => /^\s+at\s/.test(line))

  // No frames means we cannot prove it is framework-internal — let it report.
  if (frames.length === 0) return false

  return frames.every(isFrameworkFrame)
}

/**
 * The exact prefix Next.js uses for an unusable Server Action id. The full
 * message continues with the JSON-escaped id and a docs link:
 * `The Server Reference ID did not match the expected format. Received "x".`
 * (node_modules/next/dist/server/app-render/manifests-singleton.js:66), so the
 * message can only be matched by prefix — the id itself is attacker-controlled.
 */
const INVALID_SERVER_REFERENCE_ID_PREFIX =
  "The Server Reference ID did not match the expected format."

/**
 * Matches Next.js's unusable-Server-Action-id error — and nothing else.
 *
 * Next reads the `next-action` header and treats ANY POST carrying it as an
 * action invocation, whatever the content-type
 * (node_modules/next/dist/server/lib/server-action-request-meta.js:39). If the
 * value is not exactly 42 characters — Next's only format rule, see
 * node_modules/next/dist/shared/lib/server-reference-info.js:31-34 — the action
 * handler throws this error (action-handler.js:1036). Measured in production: 41
 * events in one 18-second burst from a single outside probe sending
 * `next-action: x` (also "0", "1", "action"). The client already gets a 404; no
 * route matched, no page rendered, no user exists.
 *
 * src/proxy.ts 400s these POSTs before Next ever looks an action up
 * (hasImpossibleActionId); this predicate is the backstop for the path shapes
 * that matcher never covers (e.g. `/apiary.txt`, excluded by the matcher's
 * prefix lookahead).
 *
 * Deliberately narrow: an Error (not a subclass) + the exact Next wording as a
 * prefix + at least one stack frame, with EVERY frame framework-owned per
 * isFrameworkFrame. A route handler or Server Action of ours that somehow
 * produced this message would carry a project frame — including a compiled one
 * under .next/server/app — and must keep reaching Bugsnag.
 */
export function isInvalidServerReferenceIdError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (error.name !== "Error") return false
  if (!error.message.startsWith(INVALID_SERVER_REFERENCE_ID_PREFIX)) return false

  const frames = (error.stack ?? "").split("\n").filter((line) => /^\s+at\s/.test(line))

  // No frames means we cannot prove it is framework-internal — let it report.
  if (frames.length === 0) return false

  return frames.every(isFrameworkFrame)
}

/**
 * Browser fetch-failure TypeError wordings, exact full-message matches only.
 * These are emitted by the browser itself when a connection drops mid-transfer
 * (RSC flight stream, router fetch) — no app stack frames, nothing actionable.
 *
 * Deliberately NOT included: Node/undici's "fetch failed", so server-side
 * GraphQL backend-connectivity reports (notifyGraphQLError with the original
 * cause) keep reaching the dashboard. Exact matching also keeps any wrapped or
 * prefixed message (e.g. "[GraphQL:network] CartDetail: fetch failed")
 * reporting.
 *
 * Caveat: "Failed to fetch" / "Load failed" are also what CSP- or CORS-blocked
 * fetches produce. Today no direct client-side fetch notifies Bugsnag on
 * failure (they catch and surface a toast), so nothing real is masked —
 * revisit this list if a client fetch path starts notifying.
 */
const BROWSER_NETWORK_ERROR_MESSAGES = new Set([
  "network error", // Chromium: fetch/stream died mid-transfer (BUGSNAG-2)
  "Failed to fetch", // Chromium: connection failed
  "Load failed", // WebKit
  "NetworkError when attempting to fetch resource.", // Firefox
])

/**
 * Matches known-benign browser-generated noise — and nothing else.
 *
 * Two signatures, both window-level browser artifacts rather than app bugs:
 *
 * 1. "ResizeObserver loop …" — the spec-mandated ErrorEvent the browser emits
 *    when a ResizeObserver callback mutates layout, deferring delivery by one
 *    frame (HeaderShell writes --header-h, ProductCarousel toggles arrow
 *    state; neither self-feeds). The prefix covers the current wording
 *    ("ResizeObserver loop completed with undelivered notifications.") and
 *    the legacy one ("ResizeObserver loop limit exceeded"). No real app error
 *    can start with this prefix.
 *
 * 2. Browser connectivity TypeErrors — see BROWSER_NETWORK_ERROR_MESSAGES.
 *
 * Operates on the normalized errorClass + errorMessage rather than the
 * delivery path, because the same signature arrives both via window.onerror
 * auto-detection and via manual notifyBoundaryError() from error boundaries
 * (the observed production "network error" came through GlobalError).
 */
export function isIgnorableBrowserNoise(
  errorClass: string | undefined,
  errorMessage: string | undefined,
): boolean {
  if (!errorMessage) return false
  if (errorMessage.startsWith("ResizeObserver loop")) return true
  return errorClass === "TypeError" && BROWSER_NETWORK_ERROR_MESSAGES.has(errorMessage)
}

/**
 * Matches a browser stack overflow thrown entirely by code the browser
 * injected into the page — and nothing that ran our own bundles.
 *
 * Observed on prod (Bugsnag 6aba0701396a2e952085487b / 6aba06fa396a2e95208547dc):
 * Chrome on iOS with page translation active overflowed its stack in the
 * translator's injected script. WebKit attributes injected scripts to the
 * document URL, so every frame named the page itself ("https://naturstore.lu/en",
 * lines 190-226 — inline theme CSS in our HTML, with columns past the line end),
 * never a /_next/ chunk. Replaying the visitor's path with and without simulated
 * translation raised nothing from our code.
 *
 * Every line of JavaScript we ship is served from /_next/, so requiring zero
 * /_next/ frames can never mask a recursion in our code. An empty stack is NOT
 * matched: with no frames there is no evidence about whose code it was.
 */
export function isInjectedScriptStackOverflow(
  errorClass: string | undefined,
  errorMessage: string | undefined,
  stacktrace: ReadonlyArray<{ file?: string | null }> | undefined,
): boolean {
  if (errorClass !== "RangeError") return false
  if (!errorMessage?.startsWith("Maximum call stack size exceeded")) return false
  const files = (stacktrace ?? [])
    .map((frame) => frame.file ?? "")
    .filter((file) => file !== "" && file !== "[native code]")
  if (files.length === 0) return false
  return files.every((file) => !file.includes("/_next/"))
}
